import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  lazy,
  Suspense,
  type ChangeEvent
} from "react";
import type { Editor } from "@tiptap/react";
import {
  IconButton,
  IconButtonVariant,
  IconButtonSize,
  InputSize
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  SentIcon,
  Alert02Icon,
  AttachmentIcon,
  PlusSignIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { AlertCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { useQueryClient } from "@tanstack/react-query";
import { Show } from "shared/components";
import { NoSubjectWarningModal } from "./NoSubjectWarningModal";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import {
  createInboxVariableExtension,
  buildLinkChipHtml,
  docHasUnresolvedLinkChip,
  escapeHtml,
  flattenVariableChips,
  resolveUnresolvedLinkChips,
  type LinkType
} from "./link-variable-utils";
import { InboxVariableDataContext } from "./link-variable-shared";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { cx } from "shared/utils/classnames";
import { InboxInsertVariableMenu } from "./InboxInsertVariableMenu";
import { TemplatePickerButton } from "./TemplatePickerButton";
import { TranslateComposerButton } from "./TranslateComposerButton";
import { useInPlaceTranslation } from "../hooks/useInPlaceTranslation";
import {
  PendingAttachmentPreview,
  type PendingAttachment
} from "./PendingAttachmentPreview";
import { markAttachmentUploaded, dropAttachment } from "./pending-attachment";
import {
  useSendEmail,
  useSendSms,
  useSendEmailBatch,
  useSendSmsBatch,
  useSendChat,
  useUploadChatAttachment
} from "../api/hooks";
import { fetchContactVariableValues } from "../api/crm";
import {
  docHasLinkChip,
  docHasNameChip,
  resolvePlainTextForRecipient,
  resolveHtmlForRecipient,
  type Recipient
} from "./multi-recipient";
import type {
  BatchSendResult,
  EmailBatchRecipient,
  SmsBatchRecipient
} from "../types";
import { inboxKeys } from "../api/hooks";
import {
  useContactUnitLinks,
  useContactVariableValues,
  singleLeadForUnits
} from "../api/crm";
import type { ContactUnitLinks } from "../api/crm";
import { collectDocUnitIds } from "./link-variable-utils";
import {
  useSmsVariables,
  useEmailVariables
} from "../../settings-communication/api/hooks";
import { useEnsureConversation } from "../hooks/useEnsureConversation";
import { useInboxContext } from "../context/useInboxContext";
import { toast } from "react-toastify";
import { EditorRefApi } from "shared/components/Editor";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";
import { isTextEmpty } from "shared/app/ActivityLog/helpers";
import { getSmsLength, isOverSmsLimit, MAX_SMS_LENGTH } from "utils/sms-length";
import type { ComposerTab } from "./constants";
import type { EmailTemplate } from "../../settings-communication/api/types";

const SmsTemplateSelect = lazy(() =>
  import("./SmsTemplateSelect").then((m) => ({
    default: m.SmsTemplateSelect
  }))
);

const MAX_CHAT_ATTACHMENTS = 5;

const MAX_EMAIL_ATTACHMENT_BYTES = 24 * 1024 * 1024;
const MAX_EMAIL_ATTACHMENT_TOTAL_BYTES = 24 * 1024 * 1024;

const totalAttachmentBytes = (items: PendingAttachment[]) =>
  items.reduce((sum, item) => sum + (item.sizeBytes ?? 0), 0);

// CRM-1288: SMS/MMS is image-only, one file, hard-capped at 5 MB — carriers drop
// oversized or non-image media. Twilio resizes images below this ceiling.
const MAX_SMS_ATTACHMENTS = 1;
const MAX_SMS_ATTACHMENT_BYTES = 5 * 1024 * 1024;
// Mirror communication-service's carrier-reliable allowlist exactly, so a file
// that passes here can't be rejected server-side after upload. `image/*` would
// admit SVG/ICO/HEIC that the server's magic-byte check drops.
const SMS_ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp"
];

interface ComposerDraft {
  html: string;
  text: string;
  subject: string;
}

// Unsent composer content, keyed by destination (conversation/contact +
// channel + reply target). Module-level so a draft survives composer
// unmounts within the session. Switching recipient or channel must never
// leak content between destinations — the composer is cleared on switch and
// the draft restored only when the user returns to where they started it.
const composerDrafts = new Map<string, ComposerDraft>();

const EMPTY_DRAFT: ComposerDraft = { html: "", text: "", subject: "" };

const SmsLengthNote = () => (
  <span className="flex items-center gap-1 text-danger-500">
    <HugeiconsIcon icon={AlertCircleIcon} size={14} />
    {`This message is too long to send — it must be under ${MAX_SMS_LENGTH} characters`}
  </span>
);

// Extracted to module scope to keep the component's cognitive complexity down.
const computeSendDisabled = (opts: {
  isChat: boolean;
  isSms: boolean;
  isEmail: boolean;
  isReply: boolean;
  isEmpty: boolean;
  isPending: boolean;
  isUploading: boolean;
  pendingCount: number;
  applicationId?: number;
  phoneNumberId: string;
  toPhone: string;
  toEmail: string;
  mailboxId: string;
  isToOptedOut: boolean;
  hasUnresolvedLink: boolean;
}): boolean => {
  if (opts.isChat)
    return (
      (opts.isEmpty && opts.pendingCount === 0) ||
      opts.applicationId === undefined ||
      opts.isPending ||
      opts.isUploading
    );
  if (opts.isSms)
    return (
      (opts.isEmpty && opts.pendingCount === 0) ||
      !opts.phoneNumberId ||
      !opts.toPhone ||
      opts.isToOptedOut ||
      opts.isPending ||
      opts.isUploading ||
      opts.hasUnresolvedLink
    );
  const emptyBlocks = opts.isEmail
    ? opts.isEmpty && opts.pendingCount === 0
    : opts.isEmpty;
  const uploadBlocks = opts.isEmail && opts.isUploading;
  if (opts.isReply)
    return (
      emptyBlocks ||
      !opts.mailboxId ||
      opts.isPending ||
      uploadBlocks ||
      opts.hasUnresolvedLink
    );
  return (
    emptyBlocks ||
    !opts.mailboxId ||
    !opts.toEmail ||
    opts.isPending ||
    uploadBlocks ||
    opts.hasUnresolvedLink
  );
};

const errorStatus = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status;

const BATCH_ERROR_MESSAGES: Record<string, string> = {
  invalid_recipient_address:
    "One of the selected addresses is no longer on that contact — reopen the To picker and reselect.",
  contact_not_found:
    "One of the selected contacts could not be found — reopen the To picker and reselect.",
  too_many_recipients: "You can send to up to 50 recipients at once."
};

const batchErrorMessage = (err: unknown): string | undefined => {
  const data = (err as { response?: { data?: { error?: unknown } } })?.response
    ?.data;
  return typeof data?.error === "string"
    ? BATCH_ERROR_MESSAGES[data.error]
    : undefined;
};

// The server's own error message from an axios-style 4xx rejection, so we can
// surface the specific attachment reason (not an image / too large / not
// MMS-capable) instead of a generic toast. Undefined on a transport failure.
const attachmentErrorMessage = (err: unknown): string | undefined => {
  const response = (err as { response?: { data?: { message?: unknown } } })
    ?.response;
  if (!response) return undefined;
  const message = response.data?.message;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.join(", ");
  return "Failed to send. Please try again.";
};

interface ComposerFormProps {
  channel: ComposerTab;
  conversationId?: string;
  draftContactId?: number;
  mailboxId?: string;
  toEmail?: string;
  toName?: string;
  phoneNumberId?: string;
  toPhone?: string;
  applicationId?: number;
  recipientApplicantId?: number;
  isToOptedOut?: boolean;
  subject?: string;
  replyToMessageId?: string;
  contactId?: number;
  extraRecipients?: Recipient[];
  primaryExcluded?: boolean;
  onMessageSent?: () => void;
  onTemplateSelect?: (template: EmailTemplate) => void;
}

/* eslint-disable sonarjs/cognitive-complexity -- unified composer: chat + sms + email + variable insertion + templates + opt-out */
export const ComposerForm = ({
  channel,
  conversationId,
  draftContactId,
  mailboxId = "",
  toEmail = "",
  toName,
  phoneNumberId = "",
  toPhone = "",
  applicationId,
  recipientApplicantId,
  isToOptedOut = false,
  subject: initialSubject = "",
  replyToMessageId,
  contactId,
  extraRecipients = [],
  primaryExcluded = false,
  onMessageSent,
  onTemplateSelect
}: ComposerFormProps) => {
  const queryClient = useQueryClient();
  const {
    actions: { selectConversation }
  } = useInboxContext();
  const ensureConversation = useEnsureConversation(
    conversationId,
    draftContactId
  );
  const editorRef = useRef<EditorRefApi>(null);
  const editorInstanceRef = useRef<Editor | null>(null);
  const [editorReady, setEditorReady] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [smsText, setSmsText] = useState("");
  const [hasUnresolvedLink, setHasUnresolvedLink] = useState(false);
  const [hasLinkChip, setHasLinkChip] = useState(false);
  const [subject, setSubject] = useState(initialSubject);
  const [showNoSubjectModal, setShowNoSubjectModal] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<{
    plainText: string;
    html: string;
  } | null>(null);
  const sendEmailMutation = useSendEmail();
  const sendSmsMutation = useSendSms();
  const sendEmailBatchMutation = useSendEmailBatch();
  const sendSmsBatchMutation = useSendSmsBatch();
  const sendChatMutation = useSendChat();
  const uploadAttachment = useUploadChatAttachment();
  // Inside the message pop-up the composer follows the design's compact
  // card (node 2039:32114): a bordered input box with only attach, the
  // editor and a 34px send button.
  const embed = useInboxEmbed();
  const isCompact = Boolean(embed?.compact);
  const controlSize = isCompact ? IconButtonSize.XS : IconButtonSize.SM;
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  // Mirror of pendingAttachments read after awaits in the send path, so a
  // remove-while-sending is honored rather than sending the snapshot taken
  // before `ensureConversation()` resolved.
  // Assigned during render (like draftKeyRef) so a send reads the latest staged
  // list right after an await, without an effect lagging a render behind.
  const pendingAttachmentsRef = useRef<PendingAttachment[]>([]);
  pendingAttachmentsRef.current = pendingAttachments;
  // Synchronous in-flight guard: `isPending` only flips once mutateAsync starts,
  // so on the draft-conversation flow two fast clicks would both reach the send.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const smsSendingRef = useRef(false);
  const isUploading = pendingAttachments.some((a) => a.status === "uploading");

  const isSms = channel === "sms";
  const isChat = channel === "chat";
  const isEmail = channel === "email";
  const isReply = !!replyToMessageId;
  const supportsAttachments = isChat || isEmail || isSms;
  const [isBatchSending, setIsBatchSending] = useState(false);
  const isPending =
    isBatchSending ||
    sendChatMutation.isPending ||
    sendSmsMutation.isPending ||
    sendEmailMutation.isPending ||
    sendEmailBatchMutation.isPending ||
    sendSmsBatchMutation.isPending;

  const isMulti = extraRecipients.length > 0 && (isSms || isEmail) && !isReply;
  const effectivePrimaryExcluded = primaryExcluded && isMulti;

  const contactIdStr = contactId !== undefined ? String(contactId) : undefined;
  // Same variable keys for both channels; pick the channel-appropriate source
  // so the insert menu offers the full lead variable list for email too.
  const { data: smsVariables = [] } = useSmsVariables("lead");
  const { data: emailVariables = [] } = useEmailVariables("lead");
  const variables = isSms ? smsVariables : emailVariables;

  // Units + resolved name-variable values are needed for BOTH channels now:
  // link chips resolve to a unit short URL, name chips to a resolved value.
  // Recipient of the message — used so name-variable previews (and the SMS
  // template render) resolve against the lead matching this recipient rather
  // than the contact's newest lead.
  const recipientAddress = isSms ? toPhone : toEmail;
  const { data: units = [] } = useContactUnitLinks(contactIdStr);
  const { data: variableValues = {} } = useContactVariableValues(
    contactIdStr,
    "lead",
    { address: recipientAddress || undefined }
  );

  // The contact's single unit, used to pre-select a link when there is no
  // ambiguity (exactly one unit across all the contact's leads).
  const singleUnit = useMemo(
    () => (units.length === 1 ? units[0] : null),
    [units]
  );

  // disableMentions removes the default @user mention plugin; this custom
  // extension renders variable chips (name chips display the resolved value,
  // link chips with a unit submenu) — no @-popup trigger. The extension is
  // static: chips get variables/units via InboxVariableDataContext below,
  // because the editor instantiates extensions once and would freeze any data
  // baked into their options at mount time (before the queries resolve).
  const variableExtension = useMemo(() => [createInboxVariableExtension()], []);

  const variableData = useMemo(
    () => ({ variables, units }),
    [variables, units]
  );

  // One draft per destination: an existing conversation, or a not-yet-created
  // draft conversation with a contact, further split by channel and reply
  // target so an SMS draft never shows up in the email composer (and vice
  // versa).
  const destinationKey = conversationId ?? `draft:${draftContactId ?? "none"}`;
  const draftKey = `${destinationKey}:${channel}:${replyToMessageId ?? ""}`;
  const draftKeyRef = useRef(draftKey);
  draftKeyRef.current = draftKey;

  const persistDraft = useCallback(
    (patch: Partial<ComposerDraft>) => {
      const next = {
        ...(composerDrafts.get(draftKey) ?? EMPTY_DRAFT),
        ...patch
      };
      if (!next.text.trim() && !next.subject.trim())
        composerDrafts.delete(draftKey);
      else composerDrafts.set(draftKey, next);
    },
    [draftKey]
  );

  const getComposerText = useCallback(
    () => editorRef.current?.getDocument().text ?? "",
    []
  );

  const getComposerHtml = useCallback(
    () => editorRef.current?.getDocument().html ?? "",
    []
  );

  // setEditorValue does not emit an update, so mirror applyTemplate's manual
  // bookkeeping (send-gating + draft persistence) after replacing the text.
  const applyTranslatedText = useCallback(
    (next: string) => {
      editorRef.current?.setEditorValue(next);
      const doc = editorRef.current?.getDocument();
      setIsEmpty(isTextEmpty(doc?.text ?? ""));
      if (isSms) setSmsText(doc?.text ?? "");
      setHasUnresolvedLink(docHasUnresolvedLinkChip(doc?.json));
      persistDraft({ html: doc?.html ?? "", text: doc?.text ?? next });
    },
    [isSms, persistDraft]
  );

  const formatTranslatedText = useCallback(
    (translated: string) => escapeHtml(translated).replace(/\n/g, "<br>"),
    []
  );

  const getSubjectText = useCallback(() => subject, [subject]);

  // Mirrors applyTranslatedText: translate()/undo() write the subject
  // programmatically and must not be treated as a genuine user edit.
  const applyTranslatedSubject = useCallback(
    (next: string) => {
      setSubject(next);
      persistDraft({ subject: next });
    },
    [persistDraft]
  );

  const translation = useInPlaceTranslation({
    getCurrentText: getComposerText,
    applyText: applyTranslatedText,
    getSnapshot: getComposerHtml,
    formatResult: formatTranslatedText,
    getSubjectText: isEmail ? getSubjectText : undefined,
    applySubjectText: isEmail ? applyTranslatedSubject : undefined
  });
  const canTranslate = isEmail || isSms || isChat;

  const handleSubjectChange = useCallback(
    (value: string) => {
      setSubject(value);
      persistDraft({ subject: value });
      // Subject and body translate together, so a genuine subject edit
      // must invalidate any pending undo the same way a body edit does.
      if (isEmail) translation.clearUndo();
    },
    [isEmail, persistDraft, translation.clearUndo]
  );

  // Clear/restore the composer whenever the destination changes: content
  // typed for one recipient/channel must never appear under another, but it
  // comes back when the user returns to where they started writing it.
  useEffect(() => {
    if (!editorReady) return;
    const stored = composerDrafts.get(draftKey);
    editorRef.current?.setEditorValue(stored?.html ?? "");
    setSubject(stored?.subject ?? initialSubject);
    const doc = editorRef.current?.getDocument();
    const text = doc?.text ?? "";
    setIsEmpty(isTextEmpty(text));
    setSmsText(isSms ? text : "");
    setHasUnresolvedLink(docHasUnresolvedLinkChip(doc?.json));
    setHasLinkChip(docHasLinkChip(doc?.json));
    translation.reset();
    translation.syncTargetLanguage(text);
    // Staged attachments belong to the destination they were added on — reset
    // them on any conversation/channel switch, exactly like the editor content.
    // Without this a photo staged for lead A (or on Chat) leaks into the next
    // recipient/channel and gets sent to the wrong person or rejected server-side.
    setPendingAttachments((prev) => {
      prev.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
      return [];
    });
  }, [
    draftKey,
    editorReady,
    initialSubject,
    isSms,
    translation.reset,
    translation.syncTargetLanguage
  ]);

  useEffect(
    () => () =>
      pendingAttachmentsRef.current.forEach(
        (a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl)
      ),
    []
  );

  const handleEditorChange = useCallback(() => {
    const doc = editorRef.current?.getDocument();
    if (!doc) return;

    setIsEmpty(isTextEmpty(doc.text));
    // Unresolved-link gating applies to BOTH channels: an unresolved link chip
    // serialises to an empty title, so it must block send for email too.
    setHasUnresolvedLink(docHasUnresolvedLinkChip(doc.json));
    setHasLinkChip(docHasLinkChip(doc.json));
    if (isSms) setSmsText(doc.text);
    persistDraft({ html: doc.html, text: doc.text });
    // translate()/undo() write via setEditorValue and never land here, so
    // this only fires on genuine edits.
    translation.clearUndo();
    translation.syncTargetLanguage(doc.text);
  }, [
    isSms,
    persistDraft,
    translation.clearUndo,
    translation.syncTargetLanguage
  ]);

  const handleInsertName = useCallback((key: string, value: string) => {
    const editor = editorInstanceRef.current;
    if (!editor) return;
    // `title` is what the chip serialises to (renderText). Storing the
    // resolved value makes the outgoing SMS plaintext / email body carry
    // literal text rather than the variable token.
    editor
      .chain()
      .focus()
      .insertContent({
        type: "mention",
        attrs: {
          id: key,
          entity_identifier: key,
          entity_name: "partner_user",
          title: value
        }
      })
      .insertContent(" ")
      .run();
  }, []);

  const handleInsertLink = useCallback(
    (key: LinkType, unit: ContactUnitLinks) => {
      const editor = editorInstanceRef.current;
      if (!editor) return;
      editor
        .chain()
        .focus()
        .insertContent(buildLinkChipHtml(key, unit))
        .insertContent(" ")
        .run();
    },
    []
  );

  const finalizeDraftIfNeeded = (resolvedConversationId: string) => {
    if (draftContactId !== undefined && !conversationId) {
      queryClient.invalidateQueries({ queryKey: inboxKeys.conversations() });
      selectConversation(resolvedConversationId);
    }
  };

  // The lead this message is about, derived from the units the inserted link
  // chips point at. When all resolved links belong to one lead we send it so
  // the server scopes variables + activity to that lead; otherwise we omit it
  // and the server matches by recipient address (then newest lead).
  const leadIdForDoc = useCallback(
    (json: unknown): number | undefined =>
      singleLeadForUnits(
        collectDocUnitIds(json as Parameters<typeof collectDocUnitIds>[0]).map(
          (unitId) => units.find((unit) => unit.unit_id === unitId)
        )
      ),
    [units]
  );

  // Inline email body is already fully resolved FE-side (chips serialise to
  // literal text), and its activity is lead-scoped by communication-service via
  // the recipient address, so it needs no contact_id/lead_id on the send (which
  // would trigger the boompay-api endpoint's server-side resolution + its
  // contact_has_no_lead 422). Name-variable previews are lead-correct because
  // useContactVariableValues below is passed the recipient address.
  const doSendEmail = async (doc: { text: string; html: string }) => {
    const attachmentsSnapshot = pendingAttachments;
    const attachmentIds = attachmentsSnapshot
      .map((a) => a.id)
      .filter((id): id is string => !!id);
    try {
      const resolvedConversationId = await ensureConversation();
      await sendEmailMutation.mutateAsync({
        conversation_id: resolvedConversationId,
        content_plain: doc.text,
        content_html: flattenVariableChips(doc.html),
        mailbox_id: mailboxId,
        to_address: toEmail,
        to_name: toName,
        attachment_ids: attachmentIds.length ? attachmentIds : undefined,
        ...(isReply
          ? { reply_to_message_id: replyToMessageId }
          : { subject: subject || "(No subject)" })
      });
      editorRef.current?.clearEditor();
      setIsEmpty(true);
      setPendingAttachments([]);
      attachmentsSnapshot.forEach(
        (a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl)
      );
      translation.reset();
      composerDrafts.delete(draftKey);
      finalizeDraftIfNeeded(resolvedConversationId);
      onMessageSent?.();
    } catch {
      toast.error("Failed to send email");
    }
  };

  const doSendSms = async (doc: { text: string; json?: unknown }) => {
    if (smsSendingRef.current) return;
    // Pre-flight BEFORE ensureConversation so a bail-out never leaves a freshly
    // created, message-less draft conversation behind.
    const snapshot = pendingAttachmentsRef.current;
    if (snapshot.some((a) => a.status === "uploading")) {
      toast.error("Wait for the image to finish uploading");
      return;
    }
    if (!snapshot.some((a) => !!a.id) && isTextEmpty(doc.text)) {
      toast.error("Nothing to send — the image was removed");
      return;
    }
    smsSendingRef.current = true;
    const draftKeyAtSend = draftKeyRef.current;
    let attachmentsSnapshot: PendingAttachment[] = snapshot;
    let attachmentIds: string[] = [];
    try {
      const resolvedConversationId = await ensureConversation();
      // If the composer switched destination while the conversation was being
      // created, abort — never send (or attach) to a different recipient.
      if (draftKeyRef.current !== draftKeyAtSend) return;
      // Re-read after the await so an image removed during conversation creation
      // isn't sent.
      attachmentsSnapshot = pendingAttachmentsRef.current;
      attachmentIds = attachmentsSnapshot
        .map((a) => a.id)
        .filter((id): id is string => !!id);
      await sendSmsMutation.mutateAsync({
        conversation_id: resolvedConversationId,
        to_number: toPhone,
        content: doc.text,
        phone_number_id: phoneNumberId,
        lead_id: leadIdForDoc(doc.json),
        attachment_ids: attachmentIds.length ? attachmentIds : undefined
      });
      editorRef.current?.clearEditor();
      setIsEmpty(true);
      setSmsText("");
      translation.reset();
      clearStagedAttachments(attachmentsSnapshot);
      composerDrafts.delete(draftKey);
      finalizeDraftIfNeeded(resolvedConversationId);
      onMessageSent?.();
    } catch (err) {
      // Keep the staged image on every failure: communication-service releases
      // the attachment link on any error that doesn't send (validation, a
      // pre-create S3 blip, or a failed enqueue), so a retry reuses it. Surface
      // the server's specific reason on a 400.
      const serverMessage =
        errorStatus(err) === 400 ? attachmentErrorMessage(err) : undefined;
      toast.error(
        serverMessage ??
          (attachmentIds.length
            ? "Failed to send. Please try again."
            : "Failed to send SMS")
      );
    } finally {
      smsSendingRef.current = false;
    }
  };

  const allRecipients = (address: string): Recipient[] => [
    ...(contactId !== undefined && address && !effectivePrimaryExcluded
      ? [{ contactId, contactName: toName ?? "", address }]
      : []),
    ...extraRecipients
  ];

  const followSoleRecipientConversation = (results: BatchSendResult[]) => {
    if (!effectivePrimaryExcluded) return;
    const sent = results.filter((result) => result.status === "sent");
    if (sent.length === 1 && sent[0].conversation_id)
      selectConversation(sent[0].conversation_id);
  };

  const reportBatchResults = (results: BatchSendResult[]) => {
    const sent = results.filter((r) => r.status === "sent").length;
    const skipped = results.filter((r) => r.status === "skipped").length;
    const failed = results.filter((r) => r.status === "failed").length;
    if (sent > 0)
      toast.success(
        `Sent to ${sent} recipient${sent === 1 ? "" : "s"}` +
          (skipped ? ` · ${skipped} skipped` : "") +
          (failed ? ` · ${failed} failed` : "")
      );
    else toast.error("Message could not be sent to any recipient");
  };

  const clearComposerAfterSend = () => {
    editorRef.current?.clearEditor();
    setIsEmpty(true);
    setSmsText("");
    translation.reset();
    composerDrafts.delete(draftKey);
  };

  const doSendEmailBatch = async (doc: {
    text: string;
    html: string;
    json?: unknown;
  }) => {
    if (isBatchSending) return;
    setIsBatchSending(true);
    try {
      const needsValues = docHasNameChip(doc.json);
      const recipients: EmailBatchRecipient[] = await Promise.all(
        allRecipients(toEmail).map(async (recipient) => {
          const values = needsValues
            ? await fetchContactVariableValues(
                recipient.contactId,
                recipient.address
              )
            : {};
          return {
            contact_id: recipient.contactId,
            to_address: recipient.address,
            to_name: recipient.contactName || undefined,
            subject: subject || "(No subject)",
            content_plain: resolvePlainTextForRecipient(doc.json, values),
            content_html: resolveHtmlForRecipient(doc.html, values)
          };
        })
      );
      const primary = recipients.find(
        (recipient) =>
          recipient.contact_id === contactId && recipient.to_address === toEmail
      );
      const { results } = await sendEmailBatchMutation.mutateAsync({
        mailbox_id: mailboxId,
        recipients,
        optimistic:
          conversationId && primary
            ? {
                conversation_id: conversationId,
                channel: "email",
                content_plain: primary.content_plain,
                to_address: primary.to_address,
                subject: primary.subject
              }
            : undefined
      });
      reportBatchResults(results);
      if (!results.some((result) => result.status === "sent")) return;
      clearComposerAfterSend();
      const primaryResult = results.find(
        (result) => result.contact_id === contactId && result.status === "sent"
      );
      if (!conversationId && primaryResult?.conversation_id)
        finalizeDraftIfNeeded(primaryResult.conversation_id);
      onMessageSent?.();
      followSoleRecipientConversation(results);
    } catch (err) {
      toast.error(batchErrorMessage(err) ?? "Failed to send email");
    } finally {
      setIsBatchSending(false);
    }
  };

  const doSendSmsBatch = async (doc: { text: string; json?: unknown }) => {
    if (isBatchSending) return;
    setIsBatchSending(true);
    try {
      const needsValues = docHasNameChip(doc.json);
      const recipients: SmsBatchRecipient[] = await Promise.all(
        allRecipients(toPhone).map(async (recipient) => {
          const values = needsValues
            ? await fetchContactVariableValues(
                recipient.contactId,
                recipient.address
              )
            : {};
          return {
            contact_id: recipient.contactId,
            to_number: recipient.address,
            content: resolvePlainTextForRecipient(doc.json, values)
          };
        })
      );
      if (recipients.some((recipient) => isOverSmsLimit(recipient.content))) {
        toast.error(
          "This message is too long for some recipients after personalization"
        );
        return;
      }
      const primary = recipients.find(
        (recipient) =>
          recipient.contact_id === contactId && recipient.to_number === toPhone
      );
      const { results } = await sendSmsBatchMutation.mutateAsync({
        phone_number_id: phoneNumberId,
        recipients,
        optimistic:
          conversationId && primary
            ? {
                conversation_id: conversationId,
                channel: "sms",
                content_plain: primary.content,
                to_address: primary.to_number
              }
            : undefined
      });
      reportBatchResults(results);
      if (!results.some((result) => result.status === "sent")) return;
      clearComposerAfterSend();
      const primaryResult = results.find(
        (result) => result.contact_id === contactId && result.status === "sent"
      );
      if (!conversationId && primaryResult?.conversation_id)
        finalizeDraftIfNeeded(primaryResult.conversation_id);
      onMessageSent?.();
      followSoleRecipientConversation(results);
    } catch {
      toast.error("Failed to send SMS");
    } finally {
      setIsBatchSending(false);
    }
  };

  const doSendChat = async (doc: { text: string; html: string }) => {
    if (applicationId === undefined) return;
    const attachmentsSnapshot = pendingAttachments;
    const attachmentIds = attachmentsSnapshot
      .map((a) => a.id)
      .filter((id): id is string => !!id);
    editorRef.current?.clearEditor();
    setIsEmpty(true);
    setPendingAttachments([]);
    composerDrafts.delete(draftKey);
    try {
      const resolvedConversationId = await ensureConversation();
      await sendChatMutation.mutateAsync({
        conversation_id: resolvedConversationId,
        application_id: applicationId,
        content: doc.text,
        client_message_id: crypto.randomUUID(),
        attachment_ids: attachmentIds.length ? attachmentIds : undefined,
        // Deliver the chat-to-SMS mirror to the recipient the agent picked.
        recipient_applicant_id: recipientApplicantId
      });
      translation.reset();
      attachmentsSnapshot.forEach(
        (a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl)
      );
      finalizeDraftIfNeeded(resolvedConversationId);
      onMessageSent?.();
    } catch {
      persistDraft({ html: doc.html, text: doc.text });
      const currentText = editorRef.current?.getDocument()?.text ?? "";
      if (draftKeyRef.current === draftKey && isTextEmpty(currentText)) {
        editorRef.current?.setEditorValue(doc.html);
        setIsEmpty(isTextEmpty(doc.text));
        setPendingAttachments((prev) =>
          prev.length ? prev : attachmentsSnapshot
        );
      }
      toast.error("Failed to send chat message");
    }
  };

  const uploadAttachmentFile = (file: File) => {
    const key = crypto.randomUUID();
    const isImage = file.type.startsWith("image/");
    const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
    setPendingAttachments((prev) => [
      ...prev,
      {
        key,
        filename: file.name,
        isImage,
        previewUrl,
        status: "uploading",
        sizeBytes: file.size
      }
    ]);
    uploadAttachment
      .mutateAsync(file)
      .then((att) =>
        setPendingAttachments((prev) =>
          markAttachmentUploaded(prev, key, att.id)
        )
      )
      .catch((err) => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPendingAttachments((prev) => dropAttachment(prev, key));
        toast.error(
          (err as Error).message === "file-too-large"
            ? "File is too large (max 25 MB)"
            : "Couldn't upload the file"
        );
      });
  };

  const handleFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-selecting the same file
    const remaining = MAX_CHAT_ATTACHMENTS - pendingAttachments.length;
    const overCount = files.length > remaining;
    if (overCount)
      toast.error(
        `You can attach up to ${MAX_CHAT_ATTACHMENTS} files per message`
      );
    const accepted = files.slice(0, Math.max(0, remaining));

    if (isEmail) {
      let budget =
        MAX_EMAIL_ATTACHMENT_TOTAL_BYTES -
        totalAttachmentBytes(pendingAttachments);
      const limitMb = Math.floor(
        MAX_EMAIL_ATTACHMENT_TOTAL_BYTES / (1024 * 1024)
      );
      let tooLarge = false;
      let overBudget = false;
      accepted.forEach((file) => {
        if (file.size > MAX_EMAIL_ATTACHMENT_BYTES) {
          tooLarge = true;
          return;
        }
        if (file.size > budget) {
          overBudget = true;
          return;
        }
        budget -= file.size;
        uploadAttachmentFile(file);
      });
      if (tooLarge && !overCount)
        toast.error(`Each file must be under ${limitMb} MB for email`);
      else if (overBudget && !overCount)
        toast.error(`Attachments must total under ${limitMb} MB per email`);
      return;
    }

    accepted.forEach(uploadAttachmentFile);
  };

  const handleSmsFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (pendingAttachments.length >= MAX_SMS_ATTACHMENTS) {
      toast.error("You can attach up to 1 image to a text message");
      return;
    }
    // Client-side gate only — the accept filter and these checks are
    // bypassable, so communication-service re-validates image type and size.
    // Match the server's carrier-reliable allowlist so a picked file can't pass
    // here and then 400 after upload.
    if (!SMS_ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Attach a JPEG, PNG, GIF, or WEBP image");
      return;
    }
    if (file.size === 0) {
      toast.error("That image file is empty");
      return;
    }
    if (file.size > MAX_SMS_ATTACHMENT_BYTES) {
      toast.error("Image is too large (max 5 MB)");
      return;
    }
    uploadAttachmentFile(file);
  };

  const removePendingAttachment = (key: string) =>
    setPendingAttachments((prev) => {
      const item = prev.find((a) => a.key === key);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((a) => a.key !== key);
    });

  // Remove only the given staged items (revoking just their preview URLs), so an
  // image attached after a send started isn't discarded along with the sent one.
  const clearStagedAttachments = (items: PendingAttachment[]) => {
    const keys = new Set(items.map((a) => a.key));
    setPendingAttachments((prev) => {
      prev.forEach(
        (a) =>
          keys.has(a.key) && a.previewUrl && URL.revokeObjectURL(a.previewUrl)
      );
      return prev.filter((a) => !keys.has(a.key));
    });
  };

  const handleSend = () => {
    // Chat, SMS and email may be sent with only an attachment (empty text).
    if (isEmpty && !(supportsAttachments && pendingAttachments.length > 0))
      return;

    const doc = editorRef.current?.getDocument();
    if (!doc) return;

    if (isMulti) {
      if (hasLinkChip) {
        toast.error(
          "Remove link variables to send to multiple contacts, or send individually"
        );
        return;
      }
      if (pendingAttachments.length > 0) {
        toast.error(
          "Remove the attachment to send to multiple contacts, or send individually"
        );
        return;
      }
      if (isSms) {
        if (!phoneNumberId || isOverSmsLimit(doc.text)) return;
        doSendSmsBatch(doc);
      } else {
        if (!mailboxId) return;
        if (!subject.trim()) {
          setShowNoSubjectModal(true);
          return;
        }
        doSendEmailBatch(doc);
      }
      return;
    }

    if (isChat) {
      if (applicationId !== undefined) doSendChat(doc);
      return;
    }
    if (isSms) {
      // The `hasUnresolvedLink` clause is a hard guard mirroring the email
      // path: never send an SMS that still carries an unresolved link chip,
      // regardless of button-disabled state.
      if (
        !phoneNumberId ||
        !toPhone ||
        isToOptedOut ||
        hasUnresolvedLink ||
        // Never send while the image is still uploading — otherwise an
        // attachment-only send would go out with no text and no image.
        isUploading ||
        isOverSmsLimit(doc.text)
      )
        return;
      doSendSms(doc);
    } else {
      // Never send an email that still carries an unresolved link chip,
      // mirroring the SMS guard above.
      if (!mailboxId || hasUnresolvedLink) return;
      if (isReply) {
        doSendEmail(doc);
        return;
      }
      if (!toEmail) return;
      // The pop-up has no subject field (design), so emails from it go out
      // subjectless without the warning that would otherwise fire every send.
      if (!subject.trim() && !isCompact) {
        setShowNoSubjectModal(true);
        return;
      }
      doSendEmail(doc);
    }
  };

  const applyTemplate = (plainText: string, html: string) => {
    editorRef.current?.setEditorValue(html);
    // Templates seed bare link tokens as UNRESOLVED link chips. When the
    // contact has exactly one unit, pre-select it so the user does not have
    // to disambiguate; otherwise leave them unresolved to block send.
    const editor = editorInstanceRef.current;
    if (editor && singleUnit) resolveUnresolvedLinkChips(editor, singleUnit);
    setIsEmpty(isTextEmpty(plainText));
    setSmsText(plainText);
    const doc = editorRef.current?.getDocument();
    setHasUnresolvedLink(docHasUnresolvedLinkChip(doc?.json));
    setHasLinkChip(docHasLinkChip(doc?.json));
    // setEditorValue doesn't emit an update, so persist the applied template
    // explicitly — it is unsent content just like typed text.
    persistDraft({ html: doc?.html ?? html, text: doc?.text ?? plainText });
    translation.reset();
  };

  const handleTemplateSelect = (plainText: string, html: string) => {
    if (!isEmpty) {
      setPendingTemplate({ plainText, html });
      return;
    }
    applyTemplate(plainText, html);
  };

  const smsLength = getSmsLength(smsText);
  const isOverLimit = isOverSmsLimit(smsText);

  const multiSendDisabled =
    isEmpty ||
    isPending ||
    hasLinkChip ||
    pendingAttachments.length > 0 ||
    (isSms ? !phoneNumberId || isOverLimit : !mailboxId);

  const isSendDisabled = isMulti
    ? multiSendDisabled
    : computeSendDisabled({
        isChat,
        isSms,
        isEmail,
        isReply,
        isEmpty,
        isPending,
        isUploading,
        pendingCount: pendingAttachments.length,
        applicationId,
        phoneNumberId,
        toPhone,
        toEmail,
        mailboxId,
        isToOptedOut,
        hasUnresolvedLink
      }) ||
      (isSms && isOverLimit);

  // Shared across both layouts below so /inbox keeps its single-row composer
  // unchanged while the pop-up splits into two rows.
  const attachButton = supportsAttachments ? (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={isSms ? SMS_ALLOWED_IMAGE_TYPES.join(",") : undefined}
        multiple={!isSms}
        className="hidden"
        onChange={isSms ? handleSmsFileSelected : handleFilesSelected}
      />
      <button
        type="button"
        aria-label={isSms ? "Attach image" : "Attach a file"}
        onClick={() => fileInputRef.current?.click()}
        disabled={
          pendingAttachments.length >=
          (isSms ? MAX_SMS_ATTACHMENTS : MAX_CHAT_ATTACHMENTS)
        }
        className="shrink-0 rounded-lg p-1 text-content-1000 transition-colors hover:bg-alpha-40 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <HugeiconsIcon icon={AttachmentIcon} size={20} />
      </button>
    </>
  ) : null;

  const editorBlock = (
    <div className="flex min-w-0 flex-1 flex-col">
      {/* Chip node views render in portals under the editor, so this
          provider is what feeds them variables/units (and re-renders them
          when the queries resolve). */}
      <InboxVariableDataContext.Provider value={variableData}>
        <LiteTextEditor
          ref={editorRef}
          id={`${channel}-message-editor`}
          editable
          disableMentions
          extensions={variableExtension}
          placeholder="Type your message"
          containerClassName="flex-1 text-sm text-content-1000 min-h-0 font-medium"
          onChange={handleEditorChange}
          onEditorMount={(editor) => {
            editorInstanceRef.current = editor;
            setEditorReady(true);
          }}
        />
      </InboxVariableDataContext.Provider>
      {isSms ? (
        <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs">
          <span className="tabular-nums text-content-300">
            {smsLength} characters
          </span>
          <Show if={isOverLimit}>
            <SmsLengthNote />
          </Show>
          {/* The pop-up's input card is too narrow to seat the note
              beside the editor (it squeezed the placeholder into it), so
              there it sits under the text with the counter. */}
          <Show if={isCompact && isToOptedOut}>
            <span className="font-medium text-danger-500">
              Opted out of SMS
            </span>
          </Show>
        </div>
      ) : null}
    </div>
  );

  const translateButton = (
    <Show if={canTranslate && !isEmpty && translation.targetLanguage !== null}>
      <TranslateComposerButton
        targetLanguage={translation.targetLanguage}
        canUndo={translation.canUndo}
        isTranslating={translation.isTranslating}
        onTranslate={translation.translate}
        onUndo={translation.undo}
        iconSize={controlSize}
      />
    </Show>
  );

  const sendButton = (
    <IconButton
      variant={IconButtonVariant.BASE}
      size={controlSize}
      HugeIcon={SentIcon}
      onClick={handleSend}
      disabled={isSendDisabled}
    />
  );

  return (
    <>
      <div className="flex flex-col">
        {/* The pop-up design (2152:16965) has no subject field — its email
            composer is a plain input card. */}
        {isEmail && !isReply && !isCompact ? (
          <div
            className={cx(
              isCompact ? "px-1 pb-3" : "border-b border-alpha-40 px-11 py-4"
            )}
          >
            <BaseInput
              id="email-subject"
              placeholder="Subject"
              value={subject}
              onChange={handleSubjectChange}
              size={InputSize.SM}
              autoComplete="off"
              className="[&>span]:!border-0 [&>span]:!shadow-none [&>span]:!h-auto"
              inputClassName="!p-0 border-0 shadow-none bg-transparent text-content-1000 font-medium text-sm placeholder:text-sm placeholder:font-medium placeholder:text-content-200 placeholder:not-italic"
            />
          </div>
        ) : null}

        <Show if={supportsAttachments && pendingAttachments.length > 0}>
          <div className={cx("pt-3", isCompact ? "px-1 pb-2" : "px-11")}>
            <PendingAttachmentPreview
              attachments={pendingAttachments}
              onRemove={removePendingAttachment}
            />
          </div>
        </Show>

        {isCompact ? (
          <div className="flex flex-col gap-2 rounded-lg border border-alpha-100 bg-white p-4 shadow-xs">
            <div className="flex items-center gap-2">
              {attachButton}
              {editorBlock}
            </div>
            <div className="flex items-center justify-end gap-3">
              {translateButton}
              {sendButton}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-11 py-4">
            {attachButton}
            <InboxInsertVariableMenu
              variables={variables}
              units={units}
              values={variableValues}
              onInsertName={handleInsertName}
              onInsertLink={handleInsertLink}
              trigger={
                <button
                  type="button"
                  disabled={!contactId}
                  aria-label="Insert variable"
                  className="shrink-0 rounded-lg p-1 text-content-1000 transition-colors hover:bg-alpha-40 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <HugeiconsIcon icon={PlusSignIcon} size={24} />
                </button>
              }
            />
            {editorBlock}
            <div className="flex items-center gap-3">
              {translateButton}
              {isSms && isToOptedOut ? (
                <span className="text-xs font-medium text-danger-500">
                  Opted out of SMS
                </span>
              ) : null}
              {isSms ? (
                <Suspense fallback={null}>
                  <SmsTemplateSelect
                    contactId={contactId}
                    toNumber={toPhone || undefined}
                    onSelect={handleTemplateSelect}
                  />
                </Suspense>
              ) : null}
              {/* Email templates belong to the email channel only. The old
                  !isSms && !isChat form also matched the note composer, which
                  surfaced them for partners without communication_email
                  (CRM-1132). */}
              {isEmail && onTemplateSelect ? (
                <TemplatePickerButton onSelect={onTemplateSelect} />
              ) : null}
              {sendButton}
            </div>
          </div>
        )}
      </div>

      {!isSms && !isChat ? (
        <NoSubjectWarningModal
          open={showNoSubjectModal}
          zIndex={embed?.modalZIndex}
          onClose={() => setShowNoSubjectModal(false)}
          onConfirm={() => {
            setShowNoSubjectModal(false);
            const doc = editorRef.current?.getDocument();
            if (!doc) return;
            if (isMulti) doSendEmailBatch(doc);
            else doSendEmail(doc);
          }}
        />
      ) : null}

      <ConfirmDialog
        opened={pendingTemplate !== null}
        onClose={() => setPendingTemplate(null)}
        onConfirm={() => {
          if (pendingTemplate)
            applyTemplate(pendingTemplate.plainText, pendingTemplate.html);
          setPendingTemplate(null);
        }}
        variant="warning"
        icon={{ icon: Alert02Icon, variant: "warning" }}
        title="Replace current message?"
        description="Applying this template will replace your current message."
        confirmButton={{ label: "Replace" }}
        cancelButton={{ label: "Cancel" }}
        zIndex={embed?.modalZIndex}
      />
    </>
  );
};
